import { graphHooks } from '@tunasong/graph-lib/react'
import { logger } from '@tunasong/models'
import { useCurrentUser } from '@tunasong/redux'
import type { ManyOfManyPollResponse, OptionRating, Persisted, Poll } from '@tunasong/schemas'
import { useRef, useState } from 'react'
import invariant from 'tiny-invariant'

export interface ManyOfManyResponse {
  allResponses: ManyOfManyPollResponse[]
  hasLoadedChildren: boolean
  /** The answers shown belong to someone else, so they are read-only. */
  isExternalRespondent: boolean
  choices: Record<string, boolean>
  ratings: Record<string, OptionRating>
  comments: Record<string, string>
  /** Replace the voter's choices and save them. */
  setChoices(choices: Record<string, boolean>): void
  rate(idx: number, rating: OptionRating | undefined): void
  comment(idx: number, comment: string): void
}

type ResponsePatch =
  | Pick<ManyOfManyPollResponse, 'choices'>
  | Pick<ManyOfManyPollResponse, 'ratings'>
  | Pick<ManyOfManyPollResponse, 'comments'>

/**
 * One voter's many-of-many response: what they have picked, rated and noted, saved as they go. Shared by the plain
 * and grouped vote views, so the single-create guard below exists once.
 *
 * Answers save as they are picked and stay editable until the poll closes — there is no Save button. Unsaved
 * picks used to live only in component state and vanished on navigation or reload.
 */
export const useManyOfManyResponse = (poll: Persisted<Poll>, userId: string): ManyOfManyResponse => {
  const { userId: currentUserId } = useCurrentUser()
  const { createEntity } = graphHooks.useEntityGraph()
  /** Rapid picks coalesce into one save; a pending save is flushed if the voter leaves. */
  const updateEntity = graphHooks.useEntityUpdate<ManyOfManyPollResponse>({ debounceDelay: 500 })

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })

  const savedResponse = allResponses.find(response => response.userId === userId)

  /**
   * Picks made in this session, laid over the saved copy. The children query lags every write, so it is a seed,
   * never the truth about what this voter has picked; starting from the saved answer (not `{}`) means a re-edit
   * never clears earlier picks.
   */
  const [draft, setDraft] = useState<Record<string, boolean> | undefined>(undefined)
  const choices = draft ?? savedResponse?.choices ?? {}
  /** Thumbs up / down, laid over the saved copy the same way. A sorting aid, never a vote. */
  const [ratingsDraft, setRatingsDraft] = useState<Record<string, OptionRating> | undefined>(undefined)
  const ratings = ratingsDraft ?? savedResponse?.ratings ?? {}
  /** The voter's note per option, laid over the saved copy the same way. Empty notes are not stored. */
  const [commentsDraft, setCommentsDraft] = useState<Record<string, string> | undefined>(undefined)
  const comments = commentsDraft ?? savedResponse?.comments ?? {}

  /**
   * The one create for this voter, in flight or settled, resolving to the response id. `savedResponse` cannot
   * guard against a second create: it comes from the same lagging read, so every pick before the refetch lands
   * would take the create path and write another pollresponse — and duplicates skew every percentage. It
   * resolves to `undefined` on failure (and clears itself) so the next pick can try again.
   */
  const creation = useRef<Promise<string | undefined> | null>(null)

  const createResponse = async (response: Pick<ManyOfManyPollResponse, 'choices' | 'ratings' | 'comments'>) => {
    const entity = { type: 'pollresponse', pollType: 'many-of-many', ...response } satisfies ManyOfManyPollResponse
    try {
      const created = await createEntity({ parent: poll, entity }).unwrap()
      return created.id
    } catch (error) {
      creation.current = null
      logger.warn('Failed to create the poll response', error)
      return undefined
    }
  }

  /**
   * Saves only the field that changed (`choices`, `ratings` or `comments`); the debounced update merges them. A
   * create carries all three, so a rating or comment made before any vote still starts the response in full.
   */
  const save = (patch: ResponsePatch) => {
    /** One voter, one response: once it exists, every pick updates it. */
    if (savedResponse) {
      updateEntity(savedResponse.id, patch)
      return
    }
    /** Assigned before anything can await, so two picks in one tick cannot both create. */
    if (!creation.current) {
      creation.current = createResponse({ choices, ratings, comments, ...patch })
      return
    }
    /** Picked again while the create was out: update whatever it produced. */
    void creation.current.then(id => {
      if (id) {
        updateEntity(id, patch)
      }
    })
  }

  const setChoices = (next: Record<string, boolean>) => {
    invariant(hasLoadedChildren, 'Children should be loaded')
    setDraft(next)
    save({ choices: next })
  }

  const rate = (idx: number, rating: OptionRating | undefined) => {
    invariant(hasLoadedChildren, 'Children should be loaded')
    const { [idx]: _previous, ...others } = ratings
    const next = rating ? { ...others, [idx]: rating } : others
    setRatingsDraft(next)
    save({ ratings: next })
  }

  const comment = (idx: number, text: string) => {
    invariant(hasLoadedChildren, 'Children should be loaded')
    const { [idx]: _previous, ...others } = comments
    const next = text.trim() ? { ...others, [idx]: text } : others
    setCommentsDraft(next)
    save({ comments: next })
  }

  return {
    allResponses,
    hasLoadedChildren,
    isExternalRespondent: userId !== currentUserId,
    choices,
    ratings,
    comments,
    setChoices,
    rate,
    comment,
  }
}
